import React, { useState, useCallback } from 'react';
import cn from 'classnames';

import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { getCommentsByFeedbackUuid } from 'Shared/selectors/FeedbackComments.selectors';
import { useDeepEqualSelector } from 'Shared/utils/hooks';

import FeedbackCommentEditor from './FeedbackCommentEditor/FeedbackCommentEditor';
import FeedbackCommentItem from './FeedbackCommentItem/FeedbackCommentItem';

type Props = {
  feedbackUuid: string;
  workspaceUuid: string;
  currentUserId: number;
  sectionRef: {
    current: HTMLElement;
  };
  creatorName: string;
  showEditButtonGroup: boolean;
  showGiveFeedbackButton: boolean;
  feedbackRecognitionRequestId: number;
  onGiveFeedback: Function;
};

function FeedbackCommentsArea({
  feedbackUuid,
  workspaceUuid,
  currentUserId,
  sectionRef,
  creatorName,
  showEditButtonGroup,
  showGiveFeedbackButton,
  onGiveFeedback,
  feedbackRecognitionRequestId,
}: Props) {
  const [commentEditorOptions, setCommentEditorOptions] = useState({ isOpen: false, commentUuid: null });

  const comments = useDeepEqualSelector(getCommentsByFeedbackUuid(feedbackUuid));

  const handleCommentEditorClose = useCallback(() => setCommentEditorOptions({ isOpen: false, commentUuid: null }), []);

  const handleLeaveCommentClick = useCallback(() => setCommentEditorOptions({ isOpen: true, commentUuid: null }), []);

  const handleEditCommentClick = useCallback(uuid => setCommentEditorOptions({ isOpen: true, commentUuid: uuid }), []);

  const handleGiveFeedbackClick = useCallback(() => onGiveFeedback(feedbackRecognitionRequestId), [
    onGiveFeedback,
    feedbackRecognitionRequestId,
  ]);

  return (
    <div className="feedback-comments-area border-top">
      {!commentEditorOptions.commentUuid && (
        <div
          className={cn('feedback-comments-area-body pr-4', {
            'p-3': comments.length,
            'border-bottom': commentEditorOptions.isOpen && comments.length,
          })}
        >
          {comments.map(comment => (
            <FeedbackCommentItem
              key={comment.uuid}
              comment={comment}
              onEditCommentClick={handleEditCommentClick}
              currentUserId={currentUserId}
              sectionRef={sectionRef}
            />
          ))}
        </div>
      )}
      <div className="p-3">
        {showEditButtonGroup && (
          <WpButtonGroup>
            {!commentEditorOptions.isOpen && (
              <WpButton variant={showGiveFeedbackButton ? 'primary' : 'secondary'} onClick={handleLeaveCommentClick}>
                Leave a Comment
              </WpButton>
            )}
            {!commentEditorOptions.isOpen && showGiveFeedbackButton && (
              <WpButton variant="secondary" qaTest="give-feedback-button" onClick={handleGiveFeedbackClick}>
                Give {creatorName} Feedback
              </WpButton>
            )}
          </WpButtonGroup>
        )}
        {commentEditorOptions.isOpen && (
          <FeedbackCommentEditor
            feedbackUuid={feedbackUuid}
            workspaceUuid={workspaceUuid}
            onCommentEditorClose={handleCommentEditorClose}
            commentUuid={commentEditorOptions.commentUuid}
          />
        )}
      </div>
    </div>
  );
}

export default FeedbackCommentsArea;
